import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  api, ReconnectMeta, ReconnectRow, ReconnectStateRow, useReconnectJobs,
} from '../lib/api';

function sevDot(sev: string) {
  if (sev === 'error') return 'dot dot-err';
  if (sev === 'warn') return 'dot dot-warn';
  return 'dot dot-dim';
}

function liveStatusDot(status: string) {
  if (status === 'running') return 'dot dot-run';
  if (status === 'done')    return 'dot dot-ok';
  if (status === 'failed')  return 'dot dot-err';
  return 'dot dot-dim';
}

function fmtDuration(s?: number) {
  if (s == null) return '';
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60); const r = s - m * 60;
  return `${m}m${Math.round(r)}s`;
}

export default function Reconnect() {
  const scan = useQuery({
    queryKey: ['reconnect-scan'],
    queryFn: api.reconnectScan,
    enabled: false,
    retry: false,
  });
  const jobs = useReconnectJobs();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [includeReconnecting, setIncludeReconnecting] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Pick up the most recent job_id on first load so a refresh restores
  // visibility into a still-running reconnect.
  useEffect(() => {
    if (activeJobId) return;
    const recent = jobs.data?.jobs?.[0];
    if (recent && (recent.status === 'queued' || recent.status === 'started' ||
                   recent.status === 'running' || recent.status === 'starting')) {
      setActiveJobId(recent.id);
    }
  }, [jobs.data, activeJobId]);

  const runMut = useMutation({
    mutationFn: (emails: string[]) => api.reconnectRun(emails),
    onSuccess: (out: any) => {
      setSelected(new Set());
      const id = out?.detail?.job_id;
      if (id) setActiveJobId(id);
      jobs.refetch();
    },
  });

  const rows = scan.data?.items || [];

  const bulkTargets = useMemo(() => {
    return rows
      .filter(r => r.severity === 'error' || (includeReconnecting && r.label === 'reconnecting'))
      .map(r => r.email);
  }, [rows, includeReconnecting]);

  function toggle(email: string) {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSelected(next);
  }

  async function reconnectOne(email: string) {
    await runMut.mutateAsync([email]);
  }
  async function reconnectSelected() {
    if (selected.size === 0) return;
    if (!confirm(`Reconnect ${selected.size} account(s)?`)) return;
    await runMut.mutateAsync(Array.from(selected));
  }
  async function reconnectBulk() {
    if (bulkTargets.length === 0) return;
    const label = includeReconnecting ? 'disconnected + reconnecting' : 'disconnected';
    if (!confirm(`Reconnect all ${bulkTargets.length} ${label} account(s)?`)) return;
    await runMut.mutateAsync(bulkTargets);
  }

  const jobIsLive = !!(activeJobId && jobs.data?.jobs?.find(
    j => j.id === activeJobId &&
         ['queued', 'started', 'running', 'starting'].includes(j.status))
  );
  const stats = scan.data?.stats || {};

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold">Reconnect</h1>
          <div className="text-xs text-dim">
            Scan Instantly for disconnected accounts and re-run OAuth one
            mailbox at a time. Independent of the per-tenant pipeline — safe
            to run while bootstraps are in flight.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scan.refetch()}
            disabled={scan.isFetching}
            className="btn-primary text-xs"
          >
            {scan.isFetching ? 'Scanning…' : 'Check now'}
          </button>
        </div>
      </header>

      {scan.isError && (
        <div className="card p-4 text-err text-sm">
          Scan failed: {(scan.error as Error)?.message}
        </div>
      )}

      <ActiveJobPanel
        jobId={activeJobId}
        onClose={() => setActiveJobId(null)}
        onComplete={() => { scan.refetch(); jobs.refetch(); }}
      />

      {scan.data && (
        <>
          <div className="flex items-center gap-6 text-xs text-dim flex-wrap">
            <span>scanned: <span className="text-ink">{scan.data.total_scanned}</span></span>
            <span>in DB: <span className="text-ink">{scan.data.total_in_db}</span></span>
            <span className="text-err">disconnected: {stats['disconnected'] || 0}</span>
            <span className="text-warn">reconnecting: {stats['reconnecting'] || 0}</span>
            <span>paused: {stats['paused'] || 0}</span>
            <span>connected: {stats['connected'] || 0}</span>
          </div>

          <div className="card p-3 flex items-center gap-4 flex-wrap">
            <button
              onClick={reconnectBulk}
              disabled={jobIsLive || runMut.isPending || bulkTargets.length === 0}
              className="btn-primary text-xs"
            >
              Reconnect all ({bulkTargets.length})
            </button>
            <label className="flex items-center gap-2 text-xs text-dim">
              <input
                type="checkbox"
                checked={includeReconnecting}
                onChange={e => setIncludeReconnecting(e.target.checked)}
              />
              include "Reconnecting" (Instantly auto-fix in progress)
            </label>
            <div className="flex-1" />
            <button
              onClick={reconnectSelected}
              disabled={jobIsLive || runMut.isPending || selected.size === 0}
              className="btn-primary text-xs"
            >
              Reconnect selected ({selected.size})
            </button>
          </div>

          {rows.length === 0 && (
            <div className="card p-8 text-center text-dim text-sm">
              All managed accounts are connected.
            </div>
          )}

          {rows.length > 0 && (
            <div className="card overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-dim border-b border-line">
                    <th className="p-2 w-6"></th>
                    <th className="p-2"></th>
                    <th className="p-2">Email</th>
                    <th className="p-2">Tenant</th>
                    <th className="p-2">State</th>
                    <th className="p-2">Message</th>
                    <th className="p-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: ReconnectRow) => (
                    <tr key={r.email} className="border-b border-line/50">
                      <td className="p-2">
                        <input
                          type="checkbox"
                          checked={selected.has(r.email)}
                          onChange={() => toggle(r.email)}
                        />
                      </td>
                      <td className="p-2"><span className={sevDot(r.severity)} /></td>
                      <td className="p-2 font-mono">{r.email}</td>
                      <td className="p-2">
                        <Link to={`/tenants/${r.tenant_id}`} className="underline hover:text-ink">
                          {r.target_domain}
                        </Link>
                      </td>
                      <td className="p-2">
                        {r.label}
                        <span className="text-dim"> (status={r.status})</span>
                      </td>
                      <td className="p-2 text-dim max-w-md truncate" title={r.status_message || ''}>
                        {r.status_message || '—'}
                      </td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => reconnectOne(r.email)}
                          disabled={jobIsLive || runMut.isPending}
                          className="text-xs text-run hover:underline disabled:text-dim"
                        >
                          Reconnect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!scan.data && !scan.isFetching && !scan.isError && (
        <div className="card p-8 text-center text-dim text-sm">
          Click <strong>Check now</strong> to scan Instantly for disconnected accounts.
        </div>
      )}
    </div>
  );
}


// ── live job panel ────────────────────────────────────────────────

type LiveState = { meta: ReconnectMeta; rows: Record<string, ReconnectStateRow> };

function ActiveJobPanel({
  jobId, onClose, onComplete,
}: { jobId: string | null; onClose: () => void; onComplete: () => void }) {
  const [state, setState] = useState<LiveState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!jobId) { setState(null); setError(null); completedRef.current = false; return; }
    completedRef.current = false;
    setError(null);

    let closed = false;
    api.reconnectState(jobId)
      .then(s => {
        if (closed) return;
        const rowMap: Record<string, ReconnectStateRow> = {};
        s.rows.forEach(r => { rowMap[r.email] = r; });
        setState({ meta: s.meta || {}, rows: rowMap });
      })
      .catch(() => {/* may not exist yet */});

    const es = new EventSource(`/api/reconnect/stream/${encodeURIComponent(jobId)}`);
    es.addEventListener('snapshot', (ev) => {
      if (closed) return;
      try {
        const data = JSON.parse((ev as MessageEvent).data);
        const rowMap: Record<string, ReconnectStateRow> = {};
        (data.rows || []).forEach((r: ReconnectStateRow) => { rowMap[r.email] = r; });
        setState({ meta: data.meta || {}, rows: rowMap });
      } catch { /* ignore */ }
    });
    es.addEventListener('evt', (ev) => {
      if (closed) return;
      try {
        const data = JSON.parse((ev as MessageEvent).data);
        setState(prev => mergeEvent(prev, data));
        const t = data.event;
        if ((t === 'job-end' || t === 'job-error') && !completedRef.current) {
          completedRef.current = true;
          setTimeout(onComplete, 1500);
        }
      } catch { /* ignore */ }
    });
    es.onerror = () => { setError('stream disconnected'); };

    return () => { closed = true; es.close(); };
  }, [jobId, onComplete]);

  if (!jobId) return null;
  const meta = state?.meta || {};
  const rows = Object.values(state?.rows || {});
  rows.sort((a, b) => {
    const order = { running: 0, queued: 1, failed: 2, done: 3 } as const;
    const oa = (order as any)[a.status] ?? 4;
    const ob = (order as any)[b.status] ?? 4;
    if (oa !== ob) return oa - ob;
    return a.email.localeCompare(b.email);
  });

  const total = meta.total ?? rows.length;
  const okCount = rows.filter(r => r.status === 'done').length;
  const failCount = rows.filter(r => r.status === 'failed').length;
  const runningCount = rows.filter(r => r.status === 'running').length;
  const queuedCount = rows.filter(r => r.status === 'queued').length;
  const doneness = total > 0 ? Math.round(((okCount + failCount) / total) * 100) : 0;

  const status = meta.status || 'starting';
  const isLive = status === 'running' || status === 'starting' || status === 'queued';

  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={isLive ? 'dot dot-run' : (status === 'error' ? 'dot dot-err' : 'dot dot-ok')} />
        <span className="text-sm font-semibold">Reconnect job</span>
        <span className="font-mono text-xs text-dim">{jobId}</span>
        <span className="text-xs text-dim">status: <span className="text-ink">{status}</span></span>
        <span className="text-xs text-dim">{okCount + failCount}/{total} done ({doneness}%)</span>
        {okCount > 0 && <span className="text-xs text-ok">✓ {okCount}</span>}
        {failCount > 0 && <span className="text-xs text-err">✗ {failCount}</span>}
        {runningCount > 0 && <span className="text-xs text-run">⟳ {runningCount}</span>}
        {queuedCount > 0 && <span className="text-xs text-dim">… {queuedCount}</span>}
        <div className="flex-1" />
        <button onClick={onClose} className="text-xs text-dim hover:text-ink">close</button>
      </div>

      <div className="h-1 bg-line rounded overflow-hidden">
        <div
          className={`h-full transition-all ${failCount > 0 && !isLive ? 'bg-warn' : 'bg-run'}`}
          style={{ width: `${doneness}%` }}
        />
      </div>

      {error && <div className="text-xs text-err">{error}</div>}
      {meta.error && <div className="text-xs text-err">job error: {meta.error}</div>}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-dim border-b border-line">
                <th className="p-2"></th>
                <th className="p-2">Email</th>
                <th className="p-2">Tenant</th>
                <th className="p-2">Status</th>
                <th className="p-2">Step / Error</th>
                <th className="p-2 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.email} className="border-b border-line/40">
                  <td className="p-2"><span className={liveStatusDot(r.status)} /></td>
                  <td className="p-2 font-mono">{r.email}</td>
                  <td className="p-2 text-dim">{r.target_domain || '—'}</td>
                  <td className="p-2">
                    {r.status}
                    {r.attempts != null && r.status === 'failed' && (
                      <span className="text-dim"> · {r.attempts} attempt{r.attempts === 1 ? '' : 's'}</span>
                    )}
                    {r.permanent && <span className="text-err"> · permanent</span>}
                  </td>
                  <td className="p-2 text-dim max-w-md truncate" title={r.error || r.step || ''}>
                    {r.status === 'failed' ? (r.error || '—') : (r.step || '—')}
                  </td>
                  <td className="p-2 text-right text-dim">{fmtDuration(r.duration_s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function mergeEvent(prev: LiveState | null, data: any): LiveState {
  const meta = { ...(prev?.meta || {}) };
  const rows = { ...(prev?.rows || {}) };
  const event = data.event;
  const email = data.email;
  if (event === 'queued') {
    (data.emails || []).forEach((e: string) => {
      if (!rows[e]) rows[e] = { email: e, status: 'queued' };
    });
    meta.total = data.total ?? meta.total;
  } else if (event === 'job-start') {
    meta.status = 'running';
    meta.total = data.total ?? meta.total;
  } else if (event === 'email-start' && email) {
    rows[email] = { ...(rows[email] || { email }), ...data, status: 'running', step: 'starting' };
  } else if (event === 'email-step' && email) {
    rows[email] = { ...(rows[email] || { email }), status: 'running', step: data.step };
  } else if (event === 'email-result' && email) {
    rows[email] = { ...(rows[email] || { email }), ...data, status: data.ok ? 'done' : 'failed' };
  } else if (event === 'job-end') {
    meta.status = 'done';
    meta.ok = data.ok;
    meta.failed = data.failed;
  } else if (event === 'job-error') {
    meta.status = 'error';
    meta.error = data.error;
  }
  return { meta, rows };
}
